import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { MoveApiModel } from '@app/api/api-models/MoveApiModel';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import { ImportResultApiModel } from '@api/api-models/ImportResultApiModel';
import { UploadFileService } from '@shared/services/upload-file.service';
import { HttpHeaders } from '@angular/common/http';

@Injectable()
export abstract class CrudService<TApiModel, TBaseApiModel, TId> {
  public abstract readonly apiUrl: string;

  public constructor(protected readonly http: HttpClientService) {}

  public getList(): Observable<TBaseApiModel[]> {
    return this.http.get<TBaseApiModel[]>(`${this.apiUrl}/get-list`);
  }

  public add(model: TApiModel): Observable<TApiModel> {
    return this.http.post<TApiModel>(`${this.apiUrl}/add`, model);
  }

  public update(model: TApiModel): Observable<TApiModel> {
    return this.http.put<TApiModel>(`${this.apiUrl}/update`, model);
  }

  public delete(id: TId): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/delete/${id}`);
  }

  public move(model: MoveApiModel<TId>): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/move`, model);
  }

  public getImportTemplateExample(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/get-import-template-example`, { responseType: 'blob' });
  }

  public importXlsx(file: File): Observable<ImportResultApiModel> {
    const uploadFileService = new UploadFileService();

    const payload = new FormData();
    uploadFileService.append(payload, file);

    return this.http
      .post(`${this.apiUrl}/import-xlsx`, payload, {
        responseType: 'blob',
        observe: 'response',
        headers: new HttpHeaders({
          'Skip-Interceptor': '' // custom header
        })
      })
      .pipe(
        map((data: any) => {
          const model = new ImportResultApiModel();
          model.Blob = data.body;

          if (data.headers.has('Import-Results')) {
            const value = data.headers.get('Import-Results');
            model.WasProcessed = value.split(';')[0] === 'true';
            model.FailedCount = +value.split(';')[1];
            model.ImportedCount = +value.split(';')[2];
          }

          return model;
        }),
        catchError((error) => {
          const model = new ImportResultApiModel();
          if (error.headers.has('Import-Results')) {
            const value = error.headers.get('Import-Results');
            model.WasProcessed = value.split(';')[0] === 'true';
            model.FailedCount = +value.split(';')[1];
            model.ImportedCount = +value.split(';')[2];
          }
          return of(model);
        })
      );
  }
}
